import type {
  ImportedBrepRefSourceLevel,
  ImportedPartBodyBrepBinding,
  ImportedPartBodyBrepBindingBuildResult,
  ImportedPartBodyPrimitiveDescriptor,
  ImportedTopologyFaceTriangleRangeBinding,
} from './types'

/**
 * 构建 body 绑定的稳定签名。
 *
 * 设计说明：
 * 1. 最新 topo sidecar 链路允许不再提供 BRep payload，因此 body 绑定不能强依赖 `brepRef`；
 * 2. 聚合时不能只看 bodyId，否则后端若异常导出出不同引用会被错误合并；
 * 3. 因此优先使用 BRep 指纹，缺失时使用 topo `uri/key/sha256/byteLength` 指纹，作为 body 聚合的第二维约束。
 *
 * @param bodyId 当前 primitive 对应的 bodyId。
 * @param descriptor 当前 primitive 的 BRep 描述。
 * @returns 可用于聚合的稳定键。
 */
function buildBindingGroupingKey(
  bodyId: string | undefined,
  descriptor: ImportedPartBodyPrimitiveDescriptor,
): string {
  const resourceFingerprint = descriptor.brepRef
    ? [
        'brep',
        descriptor.brepRef.uri,
        descriptor.brepRef.key,
        descriptor.brepRef.sha256,
        descriptor.brepRef.encoding,
      ].join(':')
    : descriptor.topologyRef
      ? [
          'topo',
          descriptor.topologyRef.uri,
          descriptor.topologyRef.key,
          descriptor.topologyRef.sha256 ?? '',
          descriptor.topologyRef.byteLength ?? '',
        ].join(':')
      : ''

  if (!resourceFingerprint) {
    return ''
  }

  const semanticScope = bodyId && bodyId.length > 0
    ? `body:${bodyId}`
    : resourceFingerprint

  return [
    semanticScope,
    resourceFingerprint,
  ].join('|')
}

/**
 * 构建 body 级 BRep 绑定 ID。
 *
 * 设计说明：
 * 1. 该 ID 只在前端运行时使用，不需要暴露给后端；
 * 2. 为了保证同一 mesh 内同一 body + 同一 BRep 的实例稳定，ID 中同时包含 mesh、语义范围与哈希；
 * 3. 无 bodyId 时按资源指纹聚合，避免整零件资源被多个 primitive 重复写成多个 body。
 *
 * @param meshId 当前 mesh ID。
 * @param bodyId 当前 bodyId。
 * @param resourceIdentity 当前 body 的资源身份。
 * @returns 稳定的 body 绑定 ID。
 */
function buildBodyBindingId(
  meshId: string,
  bodyId: string | undefined,
  resourceIdentity: string,
): string {
  const semanticScope = bodyId && bodyId.length > 0
    ? `body:${bodyId}`
    : `resource:${resourceIdentity}`
  return `${meshId}:${semanticScope}:${resourceIdentity}`
}

/**
 * 解析 body 绑定可用于 ID 的资源身份。
 *
 * @param descriptor 当前 primitive 描述。
 * @returns 稳定资源身份；缺失时返回空字符串。
 */
function resolveBodyBindingResourceIdentity(descriptor: ImportedPartBodyPrimitiveDescriptor): string {
  if (descriptor.brepRef) {
    return descriptor.brepRef.sha256
  }
  if (descriptor.topologyRef) {
    return descriptor.topologyRef.sha256 ?? descriptor.topologyRef.key
  }
  return ''
}

/**
 * 解析来源层级优先级。
 *
 * @param sourceLevel BRep 引用来源层级。
 * @returns 越大表示越接近真实 body 语义。
 */
function resolveSourceLevelRank(sourceLevel: ImportedBrepRefSourceLevel | undefined): number {
  if (sourceLevel === 'body') return 3
  if (sourceLevel === 'entity') return 2
  if (sourceLevel === 'mesh') return 1
  return 0
}

/**
 * 合并多个 primitive 的 BRep 来源层级。
 *
 * @param current 当前绑定已记录的来源层级。
 * @param next 新 primitive 的来源层级。
 * @returns 更接近 body 语义的来源层级。
 */
function mergeSourceLevel(
  current: ImportedBrepRefSourceLevel | undefined,
  next: ImportedBrepRefSourceLevel | undefined,
): ImportedBrepRefSourceLevel | undefined {
  return resolveSourceLevelRank(next) > resolveSourceLevelRank(current) ? next : current
}

/**
 * 合并 topo sidecar 引用。
 *
 * @param current 当前绑定已记录的 topo 引用。
 * @param next 新 primitive 的 topo 引用。
 * @returns 保守保留的 topo 引用。
 */
function mergeTopologyRef<T>(current: T | undefined, next: T | undefined): T | undefined {
  return current ?? next
}

/**
 * 构建拓扑面显式范围的去重键。
 *
 * @param binding 拓扑面范围绑定。
 * @returns 可用于去重的稳定键。
 */
function buildTopologyFaceTriangleRangeBindingKey(binding: ImportedTopologyFaceTriangleRangeBinding): string {
  return [
    binding.faceIndex ?? '',
    binding.faceRef ?? '',
    binding.subEntityId ?? '',
    binding.triangleRange[0],
    binding.triangleRange[1],
  ].join('|')
}

/**
 * 合并 primitive 粒度提供的拓扑面显式范围。
 *
 * @param target 目标 body 绑定。
 * @param source 当前 primitive 的显式范围集合。
 */
function mergeTopologyFaceTriangleRanges(
  target: ImportedPartBodyBrepBinding,
  source: ImportedTopologyFaceTriangleRangeBinding[] | undefined,
): void {
  if (!source || source.length === 0) {
    return
  }

  const merged = target.topologyFaceTriangleRanges ?? []
  const existingKeys = new Set(merged.map(buildTopologyFaceTriangleRangeBindingKey))
  source.forEach((binding) => {
    const key = buildTopologyFaceTriangleRangeBindingKey(binding)
    if (existingKeys.has(key)) {
      return
    }
    existingKeys.add(key)
    merged.push({
      faceIndex: binding.faceIndex,
      faceRef: binding.faceRef,
      subEntityId: binding.subEntityId,
      triangleRange: [binding.triangleRange[0], binding.triangleRange[1]],
    })
  })
  target.topologyFaceTriangleRanges = merged
}

/**
 * 根据 primitive 粒度描述构建 body 级 BRep 绑定。
 *
 * 设计说明：
 * 1. 导入件的默认轮廓线、局部拾取和后续拓扑代理都应以 body 为最小权威单元；
 * 2. 该函数把 mesh 遍历阶段收集的 primitive 描述纯函数化地聚合为 body 绑定，避免在 loader 中散落状态分支；
 * 3. 聚合结果同时提供 primitive 反查索引，供 visible GLB 命中快速解析到对应 body。
 *
 * @param input 构建输入。
 * @returns body 级绑定及 primitive 反向索引。
 */
export function buildImportedPartBodyBrepBindings(input: {
  meshId: string
  primitiveDescriptors: ImportedPartBodyPrimitiveDescriptor[]
}): ImportedPartBodyBrepBindingBuildResult {
  const bodyBrepBindings: Record<string, ImportedPartBodyBrepBinding> = {}
  const bodyBrepBindingIdByPrimitiveId: Record<string, string> = {}
  const bindingIdsByGroupingKey = new Map<string, string>()

  input.primitiveDescriptors.forEach((descriptor) => {
    if (!descriptor.brepRef && !descriptor.topologyRef) {
      return
    }

    const normalizedBodyId = typeof descriptor.bodyId === 'string' && descriptor.bodyId.trim().length > 0
      ? descriptor.bodyId.trim()
      : undefined
    const groupingKey = buildBindingGroupingKey(normalizedBodyId, descriptor)
    if (!groupingKey) {
      return
    }

    let bodyBindingId = bindingIdsByGroupingKey.get(groupingKey)
    if (!bodyBindingId) {
      const resourceIdentity = resolveBodyBindingResourceIdentity(descriptor)
      if (!resourceIdentity) {
        return
      }
      bodyBindingId = buildBodyBindingId(input.meshId, normalizedBodyId, resourceIdentity)
      bindingIdsByGroupingKey.set(groupingKey, bodyBindingId)
      bodyBrepBindings[bodyBindingId] = {
        bodyBindingId,
        meshId: input.meshId,
        bodyId: normalizedBodyId,
        primitiveIds: [],
        primitiveIndices: [],
        sourceLevel: descriptor.sourceLevel,
        brepBodyOrdinal: descriptor.brepBodyOrdinal,
        brepBodyKind: descriptor.brepBodyKind,
        deferBodyBrepMatch: descriptor.deferBodyBrepMatch,
        ...(descriptor.brepRef ? { brepRef: descriptor.brepRef } : {}),
        ...(descriptor.topologyRef ? { topologyRef: descriptor.topologyRef } : {}),
      }
    }

    const currentBinding = bodyBrepBindings[bodyBindingId]
    if (currentBinding.brepBodyOrdinal !== descriptor.brepBodyOrdinal || currentBinding.brepBodyKind !== descriptor.brepBodyKind) {
      throw new Error(
        `同一导入 Body 的 BRep 类型或序号不一致: bodyId=${normalizedBodyId ?? '缺失'}; `
        + `current=${currentBinding.brepBodyOrdinal ?? 'none'}; next=${descriptor.brepBodyOrdinal ?? 'none'}`,
      )
    }
    currentBinding.topologyRef = mergeTopologyRef(currentBinding.topologyRef, descriptor.topologyRef)
    currentBinding.sourceLevel = mergeSourceLevel(currentBinding.sourceLevel, descriptor.sourceLevel)
    mergeTopologyFaceTriangleRanges(currentBinding, descriptor.topologyFaceTriangleRanges)
    if (!currentBinding.primitiveIds.includes(descriptor.primitiveId)) {
      currentBinding.primitiveIds.push(descriptor.primitiveId)
    }
    if (!currentBinding.primitiveIndices.includes(descriptor.primitiveIndex)) {
      currentBinding.primitiveIndices.push(descriptor.primitiveIndex)
    }
    bodyBrepBindingIdByPrimitiveId[descriptor.primitiveId] = bodyBindingId
  })

  Object.values(bodyBrepBindings).forEach((binding) => {
    binding.primitiveIndices.sort((left, right) => left - right)
  })

  return {
    bodyBrepBindings,
    bodyBrepBindingIdByPrimitiveId,
  }
}
